import React, { useEffect } from 'react'
import styled from '@emotion/styled'

import { SummaryPageNumber, ContactPageNumber, InprogressOfferPageNumber } from '../../constants'
import { grayLight30, fontWeightSemibold, fontSize2XSmall, fontSizeLarge, fontWeightBold, fontSizeMedium, airForceBlue, white } from '../../constants/principles'
import { useTicker, useStateValue } from '../../hooks'
import { redirectToOwners } from '../../utils/kbbUtils'
import { BrowserUtils } from '../../utils/siteUtils'
import { BodyDivFlex, AnchorButton } from '../generic/Basics'
import LoaderBar from '../generic/LoaderBar'
import PageHeader from '../generic/PageHeader'
import Spinner from '../generic/spinner'
import Survey from '../generic/Survey'
import Step from './Step'
import useEventBus from '../generic/useEventBus'
import { OUTAGE_OFFER_ERROR } from '../../constants/errors'
import { getOfferErrorEventName } from '../../analytics/util'
import { usePostToParentSubscriptions } from '../../hooks/usePostToParentSubscriptions'
import { useStepTimer } from '../../hooks/useStepTimer'
import { getTheme } from '../../constants/themes'

export default ({
  experienceState: { isCombinedOffer, isAdmin, isCobrand, isAssigned, isDigitalRetail, dealerGroups, dealerGuid, isStellantisDealer, theme },
  year,
  make,
  model,
  trim,
  offerId,
  isEdit,
  offerBeingSubmitted,
  offer,
  offerError,
  patchError,
  navigatePageWithState,
  navigateToOffer,
  backClick,
  verifyRecaptcha,
  updateGlobalState,
  addImpressions,
  prospectDocuments,
  prospectId,
  ...props
}) => {
  const isDesktop = BrowserUtils.isDesktop()
  const [clickThrough, setClickThrough] = useStateValue(false)
  const kbbUrl = (props.globalState.environment && props.globalState.environment.kbb_Url) || 'https://www.kbb.com'
  const lnxCode = (props.location && props.location.initQueryParams && props.location.initQueryParams.lnx) || ''
  const firstName = props.globalState ? props.globalState.firstName : ''
  const zipcode = props.globalState ? props.globalState.zip : ''
  const hasOfferError = (offerError && +offerError > 300000) || (patchError && +patchError > 300000)
  const readyForComplete = !hasOfferError && offerId && isAdmin
  const readyForCompleteCobrand = false // Offer creation component is now always enabled, so this legacy cobrand logic is disabled
  const dataPack = prospectDocuments && prospectDocuments.documents && prospectDocuments.documents.find(document => document.canonicalName === 'replacementVehicle/supplement')
  const rvMakesModels = dataPack && dataPack.items['makesModels'].values
  const runUntilDate = new Date('July 20 2020')
  const useQuestionnaire = !isAdmin && !isAssigned && !isCobrand && !isDigitalRetail && (!runUntilDate || new Date() < runUntilDate)
  const count = useQuestionnaire ? useTicker(1500) : useTicker(600)
  const { showStep2, showStep3, isOfferReady } = useStepTimer({
    offer,
    hasOfferError
  })
  const { fontFamily, color } =  getTheme(theme) || {}

  const surveyAnswers = [
    { id: '1', text: '0 - 10 Miles' },
    { id: '2', text: '11 - 20 Miles' },
    { id: '3', text: '21 - 30 Miles' },
    { id: '4', text: '31 - 40 Miles' },
    { id: '5', text: '41 - 50 Miles' },
    { id: '6', text: '51 - 60 Miles' },
    { id: '7', text: '61 - 100 Miles' },
    { id: '8', text: '100 - 200 Miles' }
  ]

  const steps = [
    { text: 'Analyzing Your Vehicle', subtext: `${year?.displayName} ${make?.displayName} ${model?.displayName} ${trim?.displayName}`, delay: true },
    { text: 'Evaluating Condition', subtext: 'Interior, Exterior, Vehicle History, etc...', delay: showStep2 },
    { text: 'Generating Offer', subtext: `Specifically for ${firstName} in ${zipcode}`, delay: showStep3, showCheck: isOfferReady }
  ]

  useEffect(() => {
    let mounted = true

    if (mounted) {
      if (hasOfferError && !props.globalState.isAdmin) {
        verifyRecaptcha()
        handleSubmitFailure(offerError || patchError)
      }

      if (offerId && (count > 10 || clickThrough)) {
        if (!isCombinedOffer || (isCombinedOffer && BrowserUtils.isIframe())) {
          updateGlobalState({ offerIdSubmit: offer.offerId })
          setTimeout(() => navigateToOffer(offer), 500)
        } else if (BrowserUtils.isBrowser) {
          const { engine, transmission, drivetrain, color, mileage, options } = props
          const condition = offer && offer.chosenCondition

          window.location = redirectToOwners(year.displayName, make.displayName, model.displayName, trim.displayName, trim.trimId, options, engine, drivetrain, transmission, color.colorId, mileage, condition, offerId, kbbUrl, lnxCode)
        }
      }
    }

    return () => (mounted = false)
  })

  useEffect(() => {
    if (readyForComplete) {
      if (offer && offer.offerId === offerId) {
        if (isEdit) {
          BrowserUtils.postEditComplete(offerId, props.globalState.email)
        } else {
          BrowserUtils.postOfferComplete(offerId, props.globalState.email)
        }
      }
    }

    if (readyForCompleteCobrand) {
      let markupAmount
      let hasMarkup = isAssigned && dealerGuid && offer.markupAmounts && offer.markupAmounts[dealerGuid] && offer.markupAmounts[dealerGuid] > 0
      if (hasMarkup) {
        markupAmount = offer.markupAmounts[dealerGuid]
      }
      BrowserUtils.postOfferCompleteCobranding(offer, markupAmount)
    }

    if (offer && offer.offerId) {
      BrowserUtils.postOfferCompleteGoogleAnalytics(offerId)
      updateGlobalState({ fireAnalytics: 'submit_offer::success' })
    }
  }, [readyForComplete, readyForCompleteCobrand, offer, offerId, props.globalState.email, isEdit, year, make, model, rvMakesModels, dealerGroups])

  useEventBus('PostOfferCompleteCobrandingEvent', event => {
    BrowserUtils.clearLastPostMessage()
    BrowserUtils.postMessage(JSON.stringify(event), '*')
  })

  useEventBus('OfferSubmitSuccess', event => {
    updateGlobalState({ offerDataFromWebComp: event.emittedResponse, offerErrorsFromWebComp: [], termsAccepted: true })
  })

  useEventBus('OfferInProgress', handleSubmitInprogress)
  useEventBus('OfferSubmitError', handleSubmitFailure)
  useEventBus('OfferSubmitFailure', handleSubmitFailure)
  if (isStellantisDealer) {
    usePostToParentSubscriptions()
  }
  function handleSubmitInprogress(data) {
    updateGlobalState({ isOutageError: true, prospectId: data.prospectId })
    navigatePageWithState({ p: InprogressOfferPageNumber, prospectId: data.prospectId })
  }

  function handleSubmitFailure(error) {
    let p = isAdmin ? SummaryPageNumber : ContactPageNumber
    if (error === OUTAGE_OFFER_ERROR) {
      handleSubmitInprogress({ prospectId })
    } else {
      updateGlobalState({ offerError: error, offerErrorsFromWebComp: error, termsAccepted: false })
      sendErrorEvent(error)
      navigatePageWithState({ p, prospectId })
    }
  }

  function sendErrorEvent(error) {
    const { errors } = error || {}
    if (!errors) return

    errors.forEach(error => {
      const eventName = getOfferErrorEventName(error)
      if (eventName) {
        updateGlobalState({ fireAnalytics: eventName })
      }
    })
  }

  return (
    <>
      <BodyDivFlex>
        {useQuestionnaire ? (
          <>
            <div>
              <LoaderHeader isDesktop={isDesktop} fontFamily={fontFamily}>Preparing Your Offer...</LoaderHeader>
              <LoaderBar color={color} />
              <StepText>{steps[Math.min(Math.floor(count / 3), 2)].text}</StepText>
            </div>
          </>
        ) : (
          <PageHeader isDesktop={isDesktop} text={`Preparing Your Offer${'.'.repeat(count % 4)}`} theme={theme} />
        )}
        {!isAdmin ? (
          useQuestionnaire ? (
            <>
              <MarginDiv>
                <Survey
                  questionSubheader={'How far from your home would you expect to travel to redeem your Instant Cash Offer at a dealership?'}
                  answers={surveyAnswers}
                  isDesktop={isDesktop}
                  showClose={false}
                  updateGlobalState={updateGlobalState}
                  addImpressions={addImpressions}
                  surveyKey={'icosurvey_ld'}
                  backgroundColor={airForceBlue}
                  fontColor={white}
                />
                {offer && !hasOfferError && offer.offerId && (
                  <RedirectDiv>
                    If this page does not redirect in 10 seconds, <AnchorButton onClick={e => setClickThrough(true)}>Click here</AnchorButton>
                  </RedirectDiv>
                )}
              </MarginDiv>
            </>
          ) : (
            <>
              <StepContainer isDesktop={isDesktop}>
                {steps.map((step, i) => (
                  <Step
                    key={`${step.delay}${i}`}
                    isDesktop={isDesktop}
                    text={step.text}
                    subtext={step.subtext}
                    show={step.delay}
                    showCheck={step.showCheck}
                    theme={theme}
                  />
                ))}
              </StepContainer>{' '}
              {offer && !hasOfferError && offer.offerId && (
                <RedirectDiv>
                  If this page does not redirect in 10 seconds, <AnchorButton onClick={e => setClickThrough(true)}>Click here</AnchorButton>
                </RedirectDiv>
              )}
            </>
          )
        ) : (
          <SpinnerDiv>
            <Spinner size={'48px'} />
          </SpinnerDiv>
        )}
        {/* TODO: THIS NEEDS TO BE FIXED
        {offer && !hasOfferError && offer.offerId && !isCombinedOffer && !isAdmin && !useQuestionnaire && !isCobrand && !isDigitalRetail && (
          <RedirectDiv>
            If this page does not redirect in 10 seconds, <AnchorButton onClick={e => setClickThrough(true)}>Click here</AnchorButton>
          </RedirectDiv>
        )} */}
      </BodyDivFlex>
    </>
  )
}

const SpinnerDiv = styled.div`
  position: relative;
  top: 50px;
`
const RedirectDiv = styled.div`
  font-weight: ${fontWeightSemibold};
  font-size: ${fontSize2XSmall};
  flex-grow: 1;
  text-align: center;
  padding-bottom: 40px;
`
const StepContainer = styled.div`
  display: flex;
  flex-direction: ${p => (p.isDesktop ? 'row' : 'column')};
  width: 100%;
  justify-content: space-around;
  border-bottom: 1px solid ${grayLight30};
  margin-bottom: 40px;
  margin-top: 16px;
`
const MarginDiv = styled.div`
  padding-top: 32px;
`
const LoaderHeader = styled.div`
  font-size: ${p => (p.isDesktop ? fontSizeLarge : fontSizeMedium)};
  font-weight: ${fontWeightBold};
  padding: 16px;
  ${p => (p.fontFamily ? `font-family: ${p.fontFamily};` : '')};
`
const StepText = styled.div`
  text-align: center;
  padding-top: 16px;
`
